import {objectFromEntries} from '@benchling/js-utils';
import produce from 'immer';

import {Measurement} from 'constants/MeasurementConstants';
import {Info, ValueError, Warning} from 'notebook/tables/excess/Util';
import {Cell} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  ClientIdentifiableTypedValue,
  NumberTypedValue,
  UnitReferenceTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  BioentityContent,
  BioentitySource,
  ContainerSource,
  ContentGroup,
  PlateMap,
  PlateMaterialInput,
  PlateMapWell,
  ReagentAssignmentGroup,
  ReagentType,
  Source,
} from 'samples/plate-maps/PlateMapConstants';
import {
  BaseFillPlateTransferTableRowContents,
  DetailViewTransferTableColumnKey,
  FillPlateTransferTableViewMode,
  MaybeMixedMeasurementUnit,
  MaybeMixedMeasurementValue,
  MaybeMixedPlateMaterialInputId,
  MULTIPLE_VALUES_SENTINEL,
  NO_MATERIAL_INPUT_SENTINEL,
  PlateRecordViewTransferTableColumnKey,
  SharedTransferTableColumnKey,
  SummaryViewTransferTableColumnKey,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {
  MaybeMixedMaterialInputWithError,
  MaybeMixedMeasurementWithErrors,
  MeasurementWithErrors,
  ReagentValueValidationError,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import {
  getDefaultMeasurementValidationErrors,
  getDefaultMeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {PlateMapErrorLevel} from 'samples/plate-maps/validation/ValidationTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

// Functions to get mappings from column keys to indices within a row
export function getSummaryViewIndicesByColumnKey(
  showMaterialInputColumn: boolean
): Record<SharedTransferTableColumnKey | SummaryViewTransferTableColumnKey, number> {
  return objectFromEntries(
    Object.values([
      SharedTransferTableColumnKey.SOURCE_ICON,
      SummaryViewTransferTableColumnKey.SOURCE_SCHEMA,
      ...(showMaterialInputColumn ? [SharedTransferTableColumnKey.MATERIAL_INPUT] : []),
      ...Object.values(SharedTransferTableColumnKey).slice(2), // start after material input
    ]).map((columnKey, i) => [columnKey, i])
  );
}

export function getWellViewIndicesByColumnKey(
  showMaterialInputColumn: boolean
): Record<DetailViewTransferTableColumnKey | SharedTransferTableColumnKey, number> {
  return objectFromEntries(
    Object.values([
      DetailViewTransferTableColumnKey.WELL,
      SharedTransferTableColumnKey.SOURCE_ICON,
      DetailViewTransferTableColumnKey.SOURCE,
      ...(showMaterialInputColumn ? [SharedTransferTableColumnKey.MATERIAL_INPUT] : []),
      ...Object.values(SharedTransferTableColumnKey).slice(2), // start after material input
      DetailViewTransferTableColumnKey.FINAL_QUANTITY,
      DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT,
    ]).map((columnKey, i) => [columnKey, i])
  );
}

export function getPlateRecordViewIndicesByColumnKey(
  showMaterialInputColumn: boolean
): Record<
  SharedTransferTableColumnKey | PlateRecordViewTransferTableColumnKey | DetailViewTransferTableColumnKey,
  number
> {
  return objectFromEntries(
    Object.values([
      DetailViewTransferTableColumnKey.WELL,
      PlateRecordViewTransferTableColumnKey.EXPERIMENTAL_ROLE,
      PlateRecordViewTransferTableColumnKey.SOURCE_CONTAINER,
      SharedTransferTableColumnKey.SOURCE_ICON,
      DetailViewTransferTableColumnKey.SOURCE,
      ...(showMaterialInputColumn ? [SharedTransferTableColumnKey.MATERIAL_INPUT] : []),
      ...Object.values(SharedTransferTableColumnKey).slice(2), // start after material input
      DetailViewTransferTableColumnKey.FINAL_QUANTITY,
      DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT,
    ]).map((columnKey, i) => [columnKey, i])
  );
}

// Functions to extract values from cells.
export function isMultipleValuesCell(cell: Cell): boolean {
  return cell.text === MULTIPLE_VALUES_SENTINEL;
}

export function getMaterialInputIdFromCell(cell: Cell): string {
  if (cell.typedValue == null) {
    if (cell.text === '') {
      return NO_MATERIAL_INPUT_SENTINEL;
    } else {
      return cell.text;
    }
  } else if (cell.typedValue.type !== BaseTypedValueType.CLIENT_IDENTIFIABLE) {
    // This can happen if a user copy/pastes a cell from another column into this cell.
    return formatTypedValue(cell.typedValue);
  } else {
    return cell.typedValue.id;
  }
}

// Functions to extract measurement values from different types of reagents.
export function getSourceConcentration(
  reagent: Source | BioentityContent
): MeasurementWithErrors<Measurement> {
  if (reagent.type === ReagentType.SOURCE_CONTAINABLE) {
    return reagent.sourceConcentration;
  } else if (reagent.type === ReagentType.SOURCE_CONTAINER) {
    return getDefaultMeasurementWithErrors();
  } else if (reagent.initialConcentration != null) {
    return {measurement: reagent.initialConcentration, errors: getDefaultMeasurementValidationErrors()};
  } else {
    return getDefaultMeasurementWithErrors();
  }
}

/**
 * Given a source or well content and the well it's in, return the source's transfer quantity (if it's a
 * source) or the current quantity of the well (if it's a content)
 * @param reagent The source or content
 * @param well The well containing the content, or that the source is being transferred to
 */
export function getTransferQuantity(
  reagent: Source | BioentityContent,
  well: PlateMapWell
): MeasurementWithErrors<Measurement> {
  if (reagent.type === ReagentType.SOURCE_CONTAINABLE || reagent.type === ReagentType.SOURCE_CONTAINER) {
    return reagent.transferQuantity;
  } else {
    return {measurement: well.quantity, errors: getDefaultMeasurementValidationErrors()};
  }
}

export function getFinalConcentration(
  reagent: Source | BioentityContent
): MeasurementWithErrors<Measurement> {
  if (reagent.type === ReagentType.SOURCE_CONTAINABLE || reagent.type === ReagentType.WELL_CONTENT) {
    return reagent.finalConcentration;
  } else {
    return getDefaultMeasurementWithErrors();
  }
}

/**
 * Given a transfer quantity Cell, get a number (or null) to flush back to the plate map reagents' transfer
 * quantity values.
 * @param cell A table cell representing a transfer quantity
 */
export function coerceNumericValue(cell: Cell): number | null {
  let floatyText: number | null;
  if (cell.typedValue == null) {
    floatyText = parseFloat(cell.text);
  } else if (cell.typedValue.type === BaseTypedValueType.NUMBER) {
    floatyText = cell.typedValue.rawValue;
  } else {
    return null;
  }
  if (Number.isNaN(floatyText)) {
    return null;
  } else {
    return floatyText;
  }
}

/**
 * Returns true if all items in the array are the same, otherwise false.
 */
export function itemsMatch<T>(items: Array<T>): boolean {
  return new Set(items).size === 1;
}

/**
 * Get the list of "reagents" for a well.
 */
export function getReagents(well: PlateMapWell): Array<ContentGroup | Source> {
  const contentGroup: ContentGroup = {
    type: ReagentType.WELL,
    contents: well.contents,
    quantity: well.quantity,
  };
  return [...well.sources, ...(well.contents.length > 0 ? [contentGroup] : [])];
}

/**
 * Get the list of "leafs" for a reagent.
 */
export function getLeafs(
  reagent: ContentGroup | Source
): Array<BioentityContent | BioentitySource | ContainerSource> {
  return reagent.type === ReagentType.SOURCE_CONTAINABLE ||
    (reagent.type === ReagentType.SOURCE_CONTAINER && reagent.contents.length === 0)
    ? [reagent]
    : reagent.contents;
}

/**
 * Get an id representing a leaf row. This is a combination of the "schemaGroupingId" and the leaf's "groupId".
 */
export function getLeafRowId(schemaGroupingId: string, leafGroupId: string): string {
  return `${schemaGroupingId}:${leafGroupId}`;
}

/**
 * Get an id representing a "content". This is a combination of the entity's id and the well it is in.
 */
export function getContentId(well: PlateMapWell, bioentityId: string): string {
  return `id:${bioentityId},row:${well.position.row},col:${well.position.column}`;
}

/**
 * Get the intersection of two sets.
 */
export function intersect<T>(a: Set<T>, b: Set<T>): Set<T> {
  return new Set([...a].filter((itemOfA) => b.has(itemOfA)));
}

/**
 * Returns true if there is any overlap between the two sets.
 */
export function hasOverlap<T>(a: Set<T>, b: Set<T>): boolean {
  return intersect(a, b).size > 0;
}

export function getMeasurementValueCellContents(value: MaybeMixedMeasurementValue): {
  text: string;
  typedValue?: NumberTypedValue;
} {
  if (value == null || value === MULTIPLE_VALUES_SENTINEL) {
    return {text: value ?? ''};
  }

  return {
    text: '',
    typedValue: {type: BaseTypedValueType.NUMBER, rawValue: value},
  };
}

export function getMeasurementUnitCellContents(value: MaybeMixedMeasurementUnit): {
  text: string;
  typedValue?: UnitReferenceTypedValue;
} {
  if (value == null || value === MULTIPLE_VALUES_SENTINEL) {
    return {text: value ?? ''};
  }
  return {
    text: '',
    typedValue: {
      type: BaseTypedValueType.UNIT_REFERENCE,
      unitSummary: {id: value.id, name: value.name, symbol: value.symbol},
    },
  };
}

export function getMaterialInputCellContents(
  id: MaybeMixedPlateMaterialInputId,
  name: string
): {
  text: string;
  typedValue: ClientIdentifiableTypedValue;
} {
  if (id === MULTIPLE_VALUES_SENTINEL) {
    return {
      text: MULTIPLE_VALUES_SENTINEL,
      typedValue: {
        type: BaseTypedValueType.CLIENT_IDENTIFIABLE,
        id: MULTIPLE_VALUES_SENTINEL,
        name: MULTIPLE_VALUES_SENTINEL,
      },
    };
  } else {
    return {
      text: '',
      typedValue: {type: BaseTypedValueType.CLIENT_IDENTIFIABLE, id: id ?? NO_MATERIAL_INPUT_SENTINEL, name},
    };
  }
}

/**
 * For a given value with error return a partial cell value with an error (if there is one).
 */
export function getCellValueError(error: ReagentValueValidationError | null): {
  error?: ValueError | Warning | Info;
} {
  if (error == null) {
    return {};
  } else if (error.level === PlateMapErrorLevel.ERROR) {
    return {error: new ValueError(error.message)};
  } else if (error.level === PlateMapErrorLevel.WARNING) {
    return {error: new Warning(error.message)};
  } else {
    return {error: new Info(error.message)};
  }
}

function getMaterialInputNoneText(numMaterialInputs: number): string {
  return numMaterialInputs === 0 ? 'None configured' : 'None';
}

/**
 * Gets row contents that are shared between the Summary View and Well View tables.
 * @param assignmentGroup The assignment group for the reagent or schema represented by this row
 * @param containerMergeSpan When this row represents the first reagent in a container source or schema
 *                           grouping, the number of reagents in that container source or schema grouping
 * @param displayNameOverride If in Summary Mode, the name of the schema represented by this row
 * @param finalConcentration The final concentration for the reagent or schema represented by this row
 * @param isEmptyContainer Whether this row represents an empty source container
 * @param materialInput In a procedure context, the API ID of the material input associated with this row, if
 *                      any, and any errors with that material input association.
 * @param plateMaterialInputs In a procedure context, a list of plate-used material inputs.
 * @param showMaterialInputColumn Whether to include a column for configuring material input associations
 * @param sourceConcentration The source concentration for the reagent or schema represented by this row
 * @param transferQuantity The amount of this reagent or schema being transferred
 * @param viewMode The view mode for the transfer table
 */
export function getSharedRow<T extends FillPlateTransferTableViewMode>({
  assignmentGroup,
  containerMergeSpan,
  finalConcentration,
  isEmptyContainer,
  materialInput,
  plateMaterialInputs,
  reagentType,
  showMaterialInputColumn,
  sourceConcentration,
  transferQuantity,
  viewMode,
}: {
  assignmentGroup: ReagentAssignmentGroup;
  containerMergeSpan: number | null;
  finalConcentration: MaybeMixedMeasurementWithErrors;
  isEmptyContainer: boolean;
  materialInput: MaybeMixedMaterialInputWithError | null;
  plateMaterialInputs?: Array<PlateMaterialInput>;
  reagentType: ReagentType;
  showMaterialInputColumn: boolean;
  sourceConcentration: MaybeMixedMeasurementWithErrors;
  sourceId: string;
  transferQuantity: MaybeMixedMeasurementWithErrors;
  viewMode: T;
}): BaseFillPlateTransferTableRowContents<T> {
  const isSource = [ReagentType.SOURCE_CONTAINABLE, ReagentType.SOURCE_CONTAINER].includes(reagentType);
  let reagentIcon: BenchlingIconName;
  switch (reagentType) {
    case ReagentType.SOURCE_CONTAINABLE:
      reagentIcon = assignmentGroup.value.iconId;
      break;
    case ReagentType.SOURCE_CONTAINER:
      reagentIcon = 'vial';
      break;
    default:
      reagentIcon = 'tube-arrow-down';
      break;
  }
  const mergeRowSpan = containerMergeSpan != null ? {mergeRowSpan: containerMergeSpan} : {};
  const matchingMaterialInput = (plateMaterialInputs ?? []).find(
    ({versionId}) => versionId === materialInput?.materialInputId
  );
  let materialInputName: string;
  let invalidMaterialInput = false;
  if (isEmptyContainer) {
    materialInputName = 'N/A';
  } else if (matchingMaterialInput != null) {
    materialInputName = matchingMaterialInput.name;
  } else if (materialInput?.materialInputId === MULTIPLE_VALUES_SENTINEL) {
    materialInputName = MULTIPLE_VALUES_SENTINEL;
  } else if (materialInput != null && materialInput.materialInputId !== NO_MATERIAL_INPUT_SENTINEL) {
    // If there is no matching material input but one is selected, the material input doesn't exist in this
    // context.
    materialInputName = 'Invalid';
    invalidMaterialInput = true;
  } else {
    materialInputName = getMaterialInputNoneText(plateMaterialInputs?.length ?? 0);
  }

  return {
    viewMode,
    schemaId: assignmentGroup.id,
    [SharedTransferTableColumnKey.SOURCE_ICON]: {
      text: '',
      typedValue: {type: BaseTypedValueType.STRING, rawValue: reagentIcon},
      ...mergeRowSpan,
    },
    ...(showMaterialInputColumn
      ? {
          [SharedTransferTableColumnKey.MATERIAL_INPUT]: {
            ...getMaterialInputCellContents(
              materialInput?.materialInputId ?? NO_MATERIAL_INPUT_SENTINEL,
              materialInputName
            ),
            ...getCellValueError(
              // In a transfer table setting, we'll already have an error from validation. Add a fallback to
              // show an error in the details modal as well.
              // TODO(BNCH-113193): Verify this works correctly once we have the error from validaton.
              materialInput?.error ??
                (invalidMaterialInput
                  ? {level: PlateMapErrorLevel.ERROR, message: 'Invalid material input'}
                  : null)
            ),
            locked: isEmptyContainer,
          },
        }
      : {}),
    [SharedTransferTableColumnKey.SOURCE_CONCENTRATION]: {
      ...getMeasurementValueCellContents(sourceConcentration.measurement.value),
      ...getCellValueError(sourceConcentration.errors.valueError),
      locked: reagentType !== ReagentType.SOURCE_CONTAINABLE,
    },
    [SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT]: {
      ...getMeasurementUnitCellContents(sourceConcentration.measurement.unit),
      ...getCellValueError(sourceConcentration.errors.unitsError),
      locked: reagentType !== ReagentType.SOURCE_CONTAINABLE,
    },
    [SharedTransferTableColumnKey.TRANSFER_QUANTITY]: {
      ...getMeasurementValueCellContents(transferQuantity.measurement.value),
      ...mergeRowSpan,
      locked: !isSource,
      ...getCellValueError(transferQuantity.errors.valueError),
    },
    [SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT]: {
      ...getMeasurementUnitCellContents(transferQuantity.measurement.unit),
      ...mergeRowSpan,
      locked: !isSource,
      ...getCellValueError(transferQuantity.errors.unitsError),
    },
    [SharedTransferTableColumnKey.FINAL_CONCENTRATION]: {
      ...getMeasurementValueCellContents(finalConcentration.measurement.value),
      locked: isEmptyContainer,
      ...getCellValueError(finalConcentration.errors.valueError),
    },
    [SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT]: {
      ...getMeasurementUnitCellContents(finalConcentration.measurement.unit),
      locked: isEmptyContainer,
      ...getCellValueError(finalConcentration.errors.unitsError),
    },
  };
}

/**
 * Gets rows contents that represent an empty well. All values are set to null, all cells are locked.
 * @param showMaterialInputColumn Whether to include a column for configuring material input associations
 * @param viewMode The view mode for the transfer table
 * @param well The empty well to get a row for
 */
export function getEmptyWellSharedRow<T extends FillPlateTransferTableViewMode>({
  showMaterialInputColumn,
  viewMode,
  well,
}: {
  showMaterialInputColumn: boolean;
  viewMode: T;
  well?: PlateMapWell;
}): BaseFillPlateTransferTableRowContents<T> {
  return {
    viewMode,
    schemaId: '',
    [SharedTransferTableColumnKey.SOURCE_ICON]: {
      text: '',
      typedValue: {type: BaseTypedValueType.STRING, rawValue: 'tube-arrow-down'},
    },
    ...(showMaterialInputColumn
      ? {
          [SharedTransferTableColumnKey.MATERIAL_INPUT]: {
            ...getMaterialInputCellContents(NO_MATERIAL_INPUT_SENTINEL, 'N/A'),
            locked: true,
          },
        }
      : {}),
    [SharedTransferTableColumnKey.SOURCE_CONCENTRATION]: {
      ...getMeasurementValueCellContents(null),
      locked: true,
    },
    [SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT]: {
      ...getMeasurementUnitCellContents(null),
      locked: true,
    },
    [SharedTransferTableColumnKey.TRANSFER_QUANTITY]: {
      ...getMeasurementValueCellContents(well?.quantity.value ?? null),
      locked: true,
    },
    [SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT]: {
      ...getMeasurementUnitCellContents(well?.quantity.unit ?? null),
      locked: true,
    },
    [SharedTransferTableColumnKey.FINAL_CONCENTRATION]: {
      ...getMeasurementValueCellContents(null),
      locked: true,
    },
    [SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT]: {
      ...getMeasurementUnitCellContents(null),
      locked: true,
    },
  };
}

/**
 * Get material inputs to be set on entities from cells.
 */
export function getMaterialInputId(materialInputCell?: Cell | null): string | null {
  if (materialInputCell == null) {
    return null;
  }
  const materialInputId = getMaybeMixedPlateMaterialInputIdFromCell(materialInputCell);
  if (materialInputId === MULTIPLE_VALUES_SENTINEL) {
    return null;
  }
  return materialInputId;
}

/**
 * Check if a material input has changed from what is set on the entity.
 */
export function isChangedMaterialInput(
  materialInputId: string,
  entity: BioentitySource | BioentityContent
): boolean {
  return (
    (entity.materialInput != null && entity.materialInput.materialInputId !== materialInputId) ||
    (entity.materialInput == null && materialInputId !== NO_MATERIAL_INPUT_SENTINEL)
  );
}

/**
 * Given a mapping of entities to material input IDs, for each entity with exactly one material input, assign
 * it to all matching entities in the entire plate.
 */
export function assignMaterialInputsToWells(
  plateMap: PlateMap,
  materialInputsByEntityId: {[entityId: string]: Set<string>}
): PlateMap {
  const materialInputIdByEntityId = Object.entries(materialInputsByEntityId).reduce(
    (acc: {[entityId: string]: string}, [entityId, ids]) => {
      if (ids.size === 1) {
        acc[entityId] = [...ids][0];
      }
      return acc;
    },
    {}
  );
  if (Object.keys(materialInputIdByEntityId).length === 0) {
    return plateMap;
  }
  return produce(plateMap, (draft) => {
    for (const well of draft.wells) {
      for (const content of well.contents) {
        if (materialInputIdByEntityId[content.id] != null) {
          content.materialInput = {materialInputId: materialInputIdByEntityId[content.id], error: null};
        }
      }
      for (const source of well.sources) {
        if (source.type === ReagentType.SOURCE_CONTAINABLE) {
          if (materialInputIdByEntityId[source.id] != null) {
            source.materialInput = {materialInputId: materialInputIdByEntityId[source.id], error: null};
          }
        } else {
          for (const content of source.contents) {
            if (materialInputIdByEntityId[content.id] != null) {
              content.materialInput = {materialInputId: materialInputIdByEntityId[content.id], error: null};
            }
          }
        }
      }
    }
  });
}

export function getMaybeMixedPlateMaterialInputIdFromCell(
  cell: Cell
): NonNullable<MaybeMixedPlateMaterialInputId> {
  if (isMultipleValuesCell(cell)) {
    return MULTIPLE_VALUES_SENTINEL;
  } else {
    return getMaterialInputIdFromCell(cell);
  }
}
